import React, { useState } from 'react';
import { Shield, Search } from 'lucide-react';
import Input from '../../../components/Input';

export const AuditLogTab = ({ auditLogs = [], isDarkMode }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const logsArray = Array.isArray(auditLogs) ? auditLogs : [];

    const filteredLogs = logsArray.filter(log => {
        if (!log) return false;
        const s = (searchTerm || '').toLowerCase().trim();
        if (!s) return true;
        const userName = String(log.userName || '').toLowerCase();
        const action = String(log.action || '').toLowerCase();
        const entity = String(log.entity || '').toLowerCase();
        const recordId = String(log.recordId ?? '').toLowerCase();
        const userId = String(log.userId || '').toLowerCase();
        return userName.includes(s) || 
               action.includes(s) ||
               entity.includes(s) ||
               recordId.includes(s) ||
               userId.includes(s);
    }).sort((a, b) => {
        const getMs = (item) => {
            if (!item) return 0;
            if (item.timestamp?.seconds) return item.timestamp.seconds * 1000;
            if (typeof item.timestamp === 'number') return item.timestamp;
            if (item.timestamp) {
                const ms = new Date(item.timestamp).getTime();
                if (!isNaN(ms)) return ms;
            }
            if (item.date) {
                const ms = new Date(item.date).getTime();
                if (!isNaN(ms)) return ms;
            }
            return 0;
        };
        return getMs(b) - getMs(a);
    });

    const getActionStyle = (action) => {
        const act = String(action || '').toUpperCase();
        if (act.includes('DELETE') || act.includes('EXCLU')) return isDarkMode ? 'bg-red-900/40 text-red-300 border-red-800' : 'bg-red-50 text-red-700 border-red-200';
        if (act.includes('EDIT') || act.includes('ALTER') || act.includes('EDICAO')) return isDarkMode ? 'bg-orange-900/40 text-orange-300 border-orange-800' : 'bg-orange-50 text-orange-700 border-orange-200';
        return isDarkMode ? 'bg-green-900/40 text-green-300 border-green-800' : 'bg-green-50 text-green-700 border-green-200';
    };

    // Formata data e hora de forma segura suportando Timestamps do Firestore, ISO strings ou números
    const formatLogDate = (log) => {
        if (!log) return '-';
        try {
            if (log.date) {
                const d = new Date(log.date);
                if (!isNaN(d.getTime())) return d.toLocaleString('pt-BR');
            }
            if (log.timestamp) {
                if (log.timestamp?.seconds) {
                    return new Date(log.timestamp.seconds * 1000).toLocaleString('pt-BR');
                }
                const d = new Date(log.timestamp);
                if (!isNaN(d.getTime())) return d.toLocaleString('pt-BR');
            }
        } catch {}
        return '-';
    };

    // Renderiza o array de detalhes (De -> Para) dinamicamente sem risco de colisão de React child
    const renderDetails = (details) => {
        if (!details) return '-';
        let parsed = details;
        if (typeof details === 'string') {
            try {
                parsed = JSON.parse(details);
            } catch {
                return <span className="text-[10px] opacity-80 break-words">{details}</span>;
            }
        }

        if (Array.isArray(parsed)) {
            return (
                <div className="flex flex-col gap-1">
                    {parsed.map((item, idx) => {
                        if (typeof item !== 'object' || item === null) {
                            return <div key={idx} className="text-[10px]">{String(item)}</div>;
                        }
                        return (
                            <div key={idx} className={`p-1.5 rounded border text-[10px] ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
                                <strong className="text-blue-500 uppercase">{String(item.campo || 'Item')}:</strong> 
                                {item.de !== undefined && item.de !== null ? (
                                    <span className="line-through text-red-400 mx-1">{String(item.de)}</span>
                                ) : null}
                                {item.de !== undefined && item.para !== undefined ? ' ➔ ' : ''}
                                {item.para !== undefined && item.para !== null ? (
                                    <span className="text-green-500">{String(item.para)}</span>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (typeof parsed === 'object' && parsed !== null) {
            return <pre className="text-[10px] opacity-80 whitespace-pre-wrap font-sans">{JSON.stringify(parsed, null, 2)}</pre>;
        }

        return <span className="text-[10px] opacity-80">{String(parsed)}</span>;
    };

    return (
        <div className="space-y-4 h-full flex flex-col animate-fade-in p-4">
            <div className={`flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 p-3 rounded-lg border shrink-0 ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-600'}`}><Shield size={18}/></div>
                    <div>
                        <h4 className="font-bold text-sm">Auditoria do Sistema</h4>
                        <p className="text-[10px] opacity-70">Rastreabilidade completa de ações ({filteredLogs.length} registros).</p>
                    </div>
                </div>
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-2.5 text-gray-400" size={14} />
                    <Input placeholder="Buscar entidade, ID, usuário..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`pl-9 h-9 text-xs ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-gray-50 border-gray-200'}`}/>
                </div>
            </div>
            <div className={`rounded-xl shadow-sm border flex-1 overflow-hidden flex flex-col ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
                <div className="overflow-y-auto flex-1 custom-scrollbar">
                    <table className="w-full text-xs text-left">
                        <thead className={`uppercase font-bold sticky top-0 z-10 border-b ${isDarkMode ? 'bg-slate-900 text-slate-400 border-slate-700' : 'bg-gray-50 text-slate-500 border-gray-200'}`}>
                            <tr><th className="p-3">Data</th><th className="p-3">Usuário</th><th className="p-3">Entidade / ID</th><th className="p-3">Ação</th><th className="p-3">Detalhes</th></tr>
                        </thead>
                        <tbody className={`divide-y ${isDarkMode ? 'divide-slate-700' : 'divide-gray-100'}`}>
                            {filteredLogs.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="p-8 text-center text-slate-400">
                                        Nenhum registro de auditoria encontrado.
                                    </td>
                                </tr>
                            ) : (
                                filteredLogs.map((log, index) => (
                                    <tr key={log.id || index} className={`transition-colors ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'}`}>
                                        <td className="p-3 font-mono">{formatLogDate(log)}</td>
                                        <td className="p-3">
                                            <div className="font-bold">{String(log.userName || '-')}</div>
                                            <div className="text-[9px] opacity-70">{String(log.userId || '')}</div>
                                            {log.userRole && <span className="text-[9px] font-normal opacity-70 border rounded px-1 mt-0.5 inline-block">{String(log.userRole)}</span>}
                                        </td>
                                        <td className="p-3">
                                            <div className="font-bold text-indigo-500">{String(log.entity || '-')}</div>
                                            <div className="text-[10px] opacity-60 font-mono">{String(log.recordId ?? '-')}</div>
                                        </td>
                                        <td className="p-3"><span className={`inline-flex px-2 py-1 rounded text-[10px] font-bold border ${getActionStyle(log.action)}`}>{String(log.action || '-')}</span></td>
                                        <td className="p-3 min-w-[250px]">{renderDetails(log.details)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AuditLogTab;
